import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invokeFunction } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Award,
  ChevronDown,
  ChevronLeft,
  Copy,
  Check,
  Pencil,
  Globe,
  Lock,
  BookOpen,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

interface Evidence {
  id: string;
  evidence_type: string;
  source_name: string | null;
  score: number | null;
  achieved_at: string;
}

interface VerifiedSkill {
  id: string;
  skill_name: string;
  domain: string | null;
  proficiency_level: number;
  evidence_count: number;
  first_verified_at: string;
  last_verified_at: string;
  evidence: Evidence[];
}

interface SkillProfileData {
  id: string;
  user_id: string;
  public_url_slug: string;
  is_public: boolean;
  headline: string | null;
  full_name: string | null;
  email: string | null;
  updated_at: string;
}

function proficiencyColor(level: number): string {
  if (level >= 80) return "text-green-500";
  if (level >= 60) return "text-yellow-500";
  return "text-orange-500";
}

function proficiencyLabel(level: number): string {
  if (level >= 80) return "Advanced";
  if (level >= 60) return "Intermediate";
  if (level >= 40) return "Developing";
  return "Beginner";
}

function evidenceTypeLabel(type: string): string {
  switch (type) {
    case "exam_pass": return "Exam Passed";
    case "objective_mastery": return "Objective Mastered";
    case "node_completion": return "Lesson Completed";
    default: return type;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function SkillProfile() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { userType, isLoading: typeLoading } = useUserType(session?.user?.id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<SkillProfileData | null>(null);
  const [skills, setSkills] = useState<VerifiedSkill[]>([]);
  const [openSkills, setOpenSkills] = useState<Set<string>>(new Set());
  const [editingHeadline, setEditingHeadline] = useState(false);
  const [headlineDraft, setHeadlineDraft] = useState("");
  const [copied, setCopied] = useState(false);

  // Only students (learners) can view their profile; instructors are redirected to home
  useEffect(() => {
    if (!session || typeLoading) return;
    if (userType !== "learner") {
      navigate("/", { replace: true });
      return;
    }
  }, [session, userType, typeLoading, navigate]);

  useEffect(() => {
    if (!session) {
      navigate("/auth?redirect=/profile");
      return;
    }
    if (userType === "learner") {
      fetchProfile();
    }
  }, [session, userType, navigate]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await invokeFunction("get-skill-profile");
      if (error) throw error;
      setProfile(data.profile);
      setSkills(data.verifiedSkills || []);
      if (data.profile) setHeadlineDraft(data.profile.headline || "");
    } catch (err) {
      console.error("Failed to load skill profile:", err);
      toast.error("Failed to load skill profile");
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async (checked: boolean) => {
    if (!profile) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("skill_profiles")
        .update({ is_public: checked })
        .eq("id", profile.id);
      if (error) throw error;
      setProfile((p) => p ? { ...p, is_public: checked } : p);
      toast.success(checked ? "Profile is now public" : "Profile set to private");
    } catch {
      toast.error("Failed to update visibility");
    } finally {
      setSaving(false);
    }
  };

  const saveHeadline = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("skill_profiles")
        .update({ headline: headlineDraft.trim() || null })
        .eq("id", profile.id);
      if (error) throw error;
      setProfile((p) => p ? { ...p, headline: headlineDraft.trim() || null } : p);
      setEditingHeadline(false);
      toast.success("Headline updated");
    } catch {
      toast.error("Failed to update headline");
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!profile) return;
    const url = `${window.location.origin}/p/${profile.public_url_slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSkillOpen = (id: string) => {
    setOpenSkills((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex items-center gap-3">
            <Award className="h-7 w-7 text-primary" />
            <h1 className="text-xl font-bold">My Skill Profile</h1>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8 max-w-3xl space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </main>
      </div>
    );
  }

  const avgProficiency =
    skills.length > 0
      ? Math.round(skills.reduce((s, sk) => s + sk.proficiency_level, 0) / skills.length)
      : 0;

  const totalEvidence = skills.reduce((s, sk) => s + sk.evidence_count, 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/learn" className="text-muted-foreground hover:text-foreground shrink-0">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <Award className="h-6 w-6 text-primary shrink-0" />
            <h1 className="text-lg font-bold truncate">My Skill Profile</h1>
          </div>
          {profile?.is_public && (
            <Button variant="outline" size="sm" onClick={copyShareLink} className="shrink-0">
              {copied ? <Check className="h-4 w-4 mr-1.5 text-green-500" /> : <Copy className="h-4 w-4 mr-1.5" />}
              {copied ? "Copied!" : "Share link"}
            </Button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        {/* Identity card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xl font-bold text-primary shrink-0">
                {getInitials(profile?.full_name ?? null)}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold">{profile?.full_name || "Learner"}</p>

                {/* Headline */}
                {editingHeadline ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={headlineDraft}
                      onChange={(e) => setHeadlineDraft(e.target.value)}
                      placeholder="e.g. Data Analyst · Python · SQL"
                      className="h-8 text-sm"
                      maxLength={100}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveHeadline();
                        if (e.key === "Escape") setEditingHeadline(false);
                      }}
                      autoFocus
                    />
                    <Button size="sm" onClick={saveHeadline} disabled={saving}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingHeadline(false)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mt-1">
                    <p className="text-sm text-muted-foreground">
                      {profile?.headline || <span className="italic">Add a headline…</span>}
                    </p>
                    <button
                      onClick={() => setEditingHeadline(true)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* Profile URL */}
                {profile && (
                  <p className="text-xs text-muted-foreground mt-1">
                    distilled.ai/p/<span className="font-mono">{profile.public_url_slug}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Visibility toggle */}
            <div className="flex items-center gap-3 mt-5 pt-4 border-t">
              <div className="flex items-center gap-2 flex-1">
                {profile?.is_public ? (
                  <Globe className="h-4 w-4 text-green-500" />
                ) : (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
                <div>
                  <Label htmlFor="public-toggle" className="text-sm font-medium cursor-pointer">
                    {profile?.is_public ? "Public profile" : "Private profile"}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {profile?.is_public
                      ? "Anyone with the link can view your skills"
                      : "Only you can see your profile"}
                  </p>
                </div>
              </div>
              <Switch
                id="public-toggle"
                checked={profile?.is_public ?? false}
                onCheckedChange={togglePublic}
                disabled={saving}
              />
            </div>

            {profile?.is_public && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-3 w-full sm:w-auto"
                asChild
              >
                <Link to={`/p/${profile.public_url_slug}`} target="_blank">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Preview public profile
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Summary stats */}
        {skills.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardContent className="pt-4 pb-4 text-center">
                <p className="text-2xl font-bold text-primary">{skills.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Verified skills</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4 text-center">
                <p className={`text-2xl font-bold ${proficiencyColor(avgProficiency)}`}>{avgProficiency}%</p>
                <p className="text-xs text-muted-foreground mt-0.5">Avg. proficiency</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4 text-center">
                <p className="text-2xl font-bold text-blue-500">{totalEvidence}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Evidence items</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Skills list */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Verified Skills
          </h2>

          {skills.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground font-medium">No verified skills yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete course lessons and pass exams to build your skill profile.
                </p>
                <Button className="mt-4" asChild>
                  <Link to="/learn">Go to my courses</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {skills.map((skill) => (
                <Collapsible
                  key={skill.id}
                  open={openSkills.has(skill.id)}
                  onOpenChange={() => toggleSkillOpen(skill.id)}
                >
                  <Card className="overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="cursor-pointer hover:bg-muted/30 transition-colors">
                        <CardHeader className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="text-sm font-semibold leading-tight">
                                  {skill.skill_name}
                                </CardTitle>
                                {skill.domain && (
                                  <Badge variant="secondary" className="text-xs font-normal">
                                    {skill.domain}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1.5">
                                <Progress
                                  value={skill.proficiency_level}
                                  className="h-1.5 flex-1"
                                />
                                <span className={`text-xs font-medium shrink-0 ${proficiencyColor(skill.proficiency_level)}`}>
                                  {skill.proficiency_level}% · {proficiencyLabel(skill.proficiency_level)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground hidden sm:inline">
                                {skill.evidence_count} {skill.evidence_count === 1 ? "item" : "items"}
                              </span>
                              <ChevronDown
                                className={`h-4 w-4 text-muted-foreground transition-transform ${
                                  openSkills.has(skill.id) ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                          </div>
                        </CardHeader>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-3 px-4 border-t">
                        <p className="text-xs text-muted-foreground mb-2 pt-3">
                          First verified {formatDate(skill.first_verified_at)} ·
                          Last updated {formatDate(skill.last_verified_at)}
                        </p>
                        <div className="space-y-1.5">
                          {skill.evidence.map((ev) => (
                            <div
                              key={ev.id}
                              className="flex items-center justify-between text-xs bg-muted/40 rounded px-3 py-2"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Check className="h-3 w-3 text-green-500 shrink-0" />
                                <span className="truncate text-muted-foreground">{ev.source_name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                {ev.score !== null && (
                                  <span className={`font-medium ${proficiencyColor(ev.score)}`}>
                                    {ev.score}%
                                  </span>
                                )}
                                <Badge variant="outline" className="text-xs py-0">
                                  {evidenceTypeLabel(ev.evidence_type)}
                                </Badge>
                                <span className="text-muted-foreground hidden sm:inline">
                                  {formatDate(ev.achieved_at)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
